import React, { createContext, FC, PropsWithChildren, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { User } from 'models/user';
import { getCurrentUser } from 'api/users';
import Loader from 'components/Loader';

export type AuthContextType = {
    status: AuthStatus;
    user?: User;
};

export enum AuthStatus {
    Unknown,
    Authorized,
    Unauthorized,
    Forbidden,
    Error,
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const useCurrentUserQuery = () => {
    return useQuery<User>(['user', 'me'], () => getCurrentUser(), {
        useErrorBoundary: false,
    });
};

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
    const { data, isLoading, isError, error } = useCurrentUserQuery();

    if (isLoading) {
        return <Loader />;
    }

    let authStatus = AuthStatus.Unknown;
    if (isError) {
        const statusCode = (error as AxiosError).response?.status;
        if (statusCode == 401) {
            authStatus = AuthStatus.Unauthorized;
        } else if (statusCode == 403) {
            authStatus = AuthStatus.Forbidden;
        } else {
            authStatus = AuthStatus.Error;
        }
    } else {
        authStatus = AuthStatus.Authorized;
    }

    return <AuthContext.Provider value={{ status: authStatus, user: data }}>{children}</AuthContext.Provider>;
};
